/* eslint-disable react/jsx-boolean-value */
import type { FC } from 'react';
import React, { memo, useCallback, useState, useRef } from 'react';
import { css } from '@compiled/react';
import { FormattedMessage, defineMessages } from 'react-intl';
import { RelayEnvironmentProvider } from 'react-relay';
import { token } from '@atlaskit/tokens';
// eslint-disable-next-line check-choreographer-usage/no-import-in-product-messages-without-choreographer
import Popup from '@atlaskit/popup';
import Tooltip from '@atlaskit/tooltip/Tooltip';
import EditorAddIcon from '@atlaskit/icon/core/add';
import ShortcutIcon from '@atlaskit/icon/core/shortcut';
import type CardClient from '@atlaskit/link-provider/client';
import type { LinkPickerProps } from '@atlaskit/link-picker/types';
import { useSmartLinkLifecycleAnalytics } from '@atlaskit/link-analytics/lifecycle';
import withAnalyticsContext from '@atlaskit/analytics-next/withAnalyticsContext';
import IconButton from '@atlaskit/button/icon/button';
import {
	ExpandableMenuItem,
	ExpandableMenuItemTrigger,
	ExpandableMenuItemContent,
} from '@atlaskit/side-nav-items/expandable-menu-item';

import { createRelayEnvironment } from '@atlassian/relay-environment/createRelayEnvironment';
import { LoadableAfterPaint, LoadableLazy } from '@confluence/loadable';
import { useStoreOptions } from '@confluence/content-smartlinks/entry-points/useStoreOptions.ts';
import { isPageSSRd } from '@confluence/content-smartlinks/entry-points/isPageSSRd.ts';
import { fg } from '@confluence/feature-gating';
import type { WithFlagsProps } from '@confluence/flags/entry-points/withFlags.ts';
import { withFlags } from '@confluence/flags/entry-points/withFlags.ts';
import { EmptySidebarCollapsibleSectionLoader } from '@confluence/collapsible-section/entry-points/EmptySidebarCollapsibleSectionLoader.ts';
import { useSpacePersistentExpansion } from '@confluence/collapsible-section/entry-points/useSpacePersistentExpansion.ts';
import { ConfluenceSmartCardProvider } from '@confluence/smart-card-provider/entry-points/ConfluenceSmartCardProvider.tsx';
import { getRelayEnvironment } from '@confluence/graphql-provider/entry-points/relayEnvironment.ts';
import { SMART_LINK_DND_ANALYTICS_SOURCE } from '@confluence/smart-link-droppable/entry-points/smartLinkDndAnalyticsSource.ts';
import { SmartLinkDropTarget } from '@confluence/smart-link-droppable/entry-points/SmartLinkDropTarget.tsx';

import { ShortcutsHistory } from './ShortcutsHistory.ts';
import { useAddShortcut, type useShortcutsSpaceNavigationQuery_quick } from './useShortcuts.tsx';

const relayEnvironment = createRelayEnvironment({});

const ShortcutsDragContainer = LoadableAfterPaint({
	loader: async () =>
		(
			await import(
				/* webpackChunkName: "loadable-ShortcutsDragContainer" */ './ShortcutsDragContainer.tsx'
			)
		).ShortcutsDragContainer,
});

const LinkPickerWithPlugin = LoadableLazy({
	loader: async () =>
		(
			await import(
				/* webpackChunkName: "loadable-LazyLinkPickerWithPlugin" */ './LinkPickerWithPlugin.tsx'
			)
		).LinkPickerWithPlugin,
});

const linksSectionContainerStyles = css({
	position: 'relative',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	'a:visited, a:focus, a:active': {
		textDecoration: 'none',
		color: token('color.link.pressed'),
	},
});

// Reserve a small top inset so the shortcuts drag outline is fully visible without changing layout.
const shortcutsDropTargetContainerStyles = css({
	paddingTop: token('space.025'),
	marginBottom: `calc(-1 * ${token('space.025')})`,
});

const shortcutsDropTargetStyles = css({
	marginTop: '0',
	marginRight: `calc(-1 * (${token('space.100')} + ${token('space.025')}))`,
	marginBottom: `calc(-1 * ${token('space.025')})`,
	paddingTop: '0',
	paddingRight: `calc(${token('space.100')} + ${token('space.025')})`,
	paddingBottom: token('space.025'),
});

const i18n = defineMessages({
	spaceShortcutsHeader: {
		id: 'space-shortcuts.shortcuts.header',
		defaultMessage: 'Shortcuts',
		description:
			'The text is shown as a section heading above the list of shortcuts in a Confluence space sidebar or shortcuts panel.',
	},
	spaceShortcutsAddLinkTooltip: {
		id: 'space-shortcuts.shortcuts.add.tooltip',
		defaultMessage: 'Add shortcut',
		description: 'Tooltip for button to add a shortcut using the link picker',
	},
	shortcutsTooltip: {
		id: 'space-shortcuts.side-nav-collapsible-header.tooltip',
		defaultMessage: 'Quick links to Confluence or web content',
		description: 'Tooltip when hovering over shortcuts space sidebar collapsible section',
	},
	noShortcutsMessage: {
		id: 'space-shortcuts.no-shortcuts.message',
		defaultMessage: 'No shortcuts in this space',
		description: 'Message when shortcuts section is empty for admins',
	},
});

type ShortcutsSectionProps = {
	isSpaceAdmin: boolean;
	spaceId: string | null;
	spaceKey: string;
	links: useShortcutsSpaceNavigationQuery_quick[];
	client?: CardClient | undefined;
};

const ShortcutsSectionComponent: FC<ShortcutsSectionProps & WithFlagsProps> = memo(
	({ flags, isSpaceAdmin, links, spaceId, spaceKey }) => {
		const shortcutsHistoryRef = useRef(new ShortcutsHistory());
		const [isPopupOpen, setIsPopupOpen] = useState(false);

		const handlePopupClose = useCallback(() => {
			setIsPopupOpen(false);
		}, [setIsPopupOpen]);

		const { addShortcut, undoAddShortcut } = useAddShortcut(links, flags, shortcutsHistoryRef);

		const { linkCreated } = useSmartLinkLifecycleAnalytics();

		// collapsed by default if there are no shortcuts
		const { isExpanded, onExpansionToggle } = useSpacePersistentExpansion({
			sectionName: 'shortcuts',
			collapsedByDefault: links.length === 0,
		});

		const onSubmit: LinkPickerProps['onSubmit'] = useCallback(
			(payload, analytic) => {
				const { url, displayText: title } = payload;
				// hides previous success flag so that only one is on the screen at a time
				const lastMovement = shortcutsHistoryRef.current.peek();
				if (lastMovement) {
					void flags.hideFlag(lastMovement.flagId);
				}

				const item = {
					url,
					title,
				};
				addShortcut(item);

				// needed for undo functionality
				shortcutsHistoryRef.current.push({
					undo: () => undoAddShortcut(item as any),
					lastDeletedItem: item,
				});
				handlePopupClose();

				linkCreated(payload, analytic);
			},
			[handlePopupClose, addShortcut, undoAddShortcut, flags, linkCreated],
		);

		const onSmartLinkDropped = useCallback(
			({ url, title }: { url: string; title?: string }) => {
				const lastMovement = shortcutsHistoryRef.current.peek();
				if (lastMovement) {
					void flags.hideFlag(lastMovement.flagId);
				}

				const item = { url, title: title ?? null };
				addShortcut(item, SMART_LINK_DND_ANALYTICS_SOURCE);

				shortcutsHistoryRef.current.push({
					undo: () =>
						undoAddShortcut(
							item as useShortcutsSpaceNavigationQuery_quick,
							SMART_LINK_DND_ANALYTICS_SOURCE,
						),
					lastDeletedItem: item,
				});
			},
			[addShortcut, undoAddShortcut, flags],
		);

		const renderAddShortcutButton = useCallback(
			() => (
				// TODO: Add `shouldRenderToParent` to component to improve accessibility. See
				// https://a11y.atlassian.net/browse/JCATS-788
				// https://hello.atlassian.net/wiki/spaces/DST/pages/5402216605/shouldRenderToParent+prop+usages+in+cc-disco+packages
				<Popup
					autoFocus={false}
					isOpen={isPopupOpen}
					onClose={() => handlePopupClose()}
					content={({ update }) => (
						<LinkPickerWithPlugin
							onSubmit={onSubmit}
							onCancel={handlePopupClose}
							onContentResize={update}
						/>
					)}
					placement="bottom-start"
					trigger={(triggerProps) => {
						return (
							<Tooltip
								content={<FormattedMessage {...i18n.spaceShortcutsAddLinkTooltip} />}
								position="top"
								hideTooltipOnMouseDown
							>
								<IconButton
									icon={(iconProps) => <EditorAddIcon {...iconProps} size="small" />}
									label={<FormattedMessage {...i18n.spaceShortcutsAddLinkTooltip} />}
									onClick={() => setIsPopupOpen((v) => !v)}
									spacing="compact"
									appearance="subtle"
									{...triggerProps}
								/>
							</Tooltip>
						);
					}}
				/>
			),
			[isPopupOpen, handlePopupClose, onSubmit],
		);

		// Show nothing if the user is not a space admin and there are no shortcuts
		if (!isSpaceAdmin && links.length == 0) {
			return null;
		}

		const menuContent =
			links.length > 0 ? (
				<div css={linksSectionContainerStyles}>
					<ShortcutsDragContainer
						isSpaceAdmin={isSpaceAdmin}
						links={links}
						spaceId={spaceId}
						spaceKey={spaceKey}
						shortcutsHistoryRef={shortcutsHistoryRef}
					/>
				</div>
			) : (
				<EmptySidebarCollapsibleSectionLoader>
					<FormattedMessage {...i18n.noShortcutsMessage} />
				</EmptySidebarCollapsibleSectionLoader>
			);

		return (
			<div css={shortcutsDropTargetContainerStyles}>
				<SmartLinkDropTarget onDropped={onSmartLinkDropped} css={shortcutsDropTargetStyles}>
					<ExpandableMenuItem
						isExpanded={isExpanded}
						onExpansionToggle={onExpansionToggle}
						testId="page-tree-short-cuts"
					>
						<ExpandableMenuItemTrigger
							elemBefore={<ShortcutIcon label="" color="currentColor" />}
							actionsOnHover={isSpaceAdmin && renderAddShortcutButton()}
						>
							<FormattedMessage {...i18n.spaceShortcutsHeader} />
						</ExpandableMenuItemTrigger>
						<ExpandableMenuItemContent>{menuContent}</ExpandableMenuItemContent>
					</ExpandableMenuItem>
				</SmartLinkDropTarget>
			</div>
		);
	},
);

const ShortcutsSectionWrapper = withAnalyticsContext({
	attributes: {
		location: 'confluenceShortcuts',
	},
})(withFlags(ShortcutsSectionComponent));

export const ShortcutsSection: FC<ShortcutsSectionProps> = (props) => {
	const initialState = window['__SSR_SHORTCUTS__'];
	const storeData = useStoreOptions(initialState, isPageSSRd());
	const storeOptions = isPageSSRd() ? storeData : {};

	const content = <ShortcutsSectionWrapper {...props} />;

	return (
		<RelayEnvironmentProvider
			environment={fg('cfe_use_confluence_relay_env') ? getRelayEnvironment() : relayEnvironment}
		>
			<ConfluenceSmartCardProvider
				client={props.client}
				authFlow="disabled"
				doNotUsePreviewPanels={true}
				{...storeOptions}
			>
				{content}
			</ConfluenceSmartCardProvider>
		</RelayEnvironmentProvider>
	);
};
